import axios from 'axios';
import { useEffect, useState } from 'react';
import { ContentCard } from '../../../components/ContentCard/ContentCard';
import { Button } from '../../../components/Form/Button';
import { Icon, chevron_left, close } from '../../../components/Icon/Icon';
import { Modal } from '../../../components/Modal/Modal';
import { AlertMessages, useAlerts } from '../../AlertMessages/AlertMessages';
import { WITH_CREDENTIALS } from '../../../components/utils';
import { useConfig } from '../../../config';
import { useImmer } from '../../../hooks/useImmer';
import { useTranslation } from '../../../i18n/i18n';
import { Res, useApi } from '../../../state/api';
import { useSession } from '../../../state/session';
import { ApiDeliveryAddress, ApiList, ApiSchedule, ScheduleStatus } from '../../../types';
import { calculateNextOrderDate, findNextValidDay, formatCalendarDate, formatScheduleText } from '../utils';
import { WizardStepLabel } from '../WizardStepLabel';
import { WizardControl, statusOf } from '../wizard';
import { EditScheduleForm } from './EditScheduleForm';
import { SelectSuggestions } from './SelectSuggestions';

export const EditScheduleModal = ({
	list,
	deleting,
	wizard,
	pendingAddress,
	dialogOnly,
}: {
	list: ApiList;
	deleting?: boolean;
	/* the draft card lists the steps itself and wants nothing from this but the dialog */
	dialogOnly?: boolean;
	/* set while the wizard is on this step, which is also what holds the modal open */
	wizard?: WizardControl;
	/* an address chosen a step earlier, which there was no schedule to hang it on yet */
	pendingAddress?: ApiDeliveryAddress;
}): JSX.Element => {
	const { locale, api } = useConfig();
	const t = { ...useTranslation().subscriptions, generic: useTranslation().generic };
	const { data, mutate } = useApi(Res.schedule, { listId: list.id });

	const [hasSchedule, setHasSchedule] = useState(false);
	const { user } = useSession();
	const startDate = findNextValidDay();

	const DEFAULT_SCHEDULE = {
		id: 0,
		startDate: formatCalendarDate(startDate),
		weeklyDays: '[]',
		monthlyInterval: 'date',
		monthlyDay: 1,
		recurrenceValue: 1,
		productListId: parseInt(list.id),
	} as ApiSchedule;
	const [schedule, setSchedule] = useImmer<ApiSchedule>(DEFAULT_SCHEDULE);
	const [hasDeliveryAddress, setHasDeliveryAddress] = useState(false);
	useEffect(() => {
		if (data?.deliveryAddress && data?.deliveryAddress.id != 0) {
			setHasDeliveryAddress(true);
		} else {
			setHasDeliveryAddress(false);
		}
		if (data) {
			setHasSchedule(true);
			return setSchedule(data);
		}
		setHasSchedule(false);
	}, [data]);

	const [loading, setLoading] = useState(false);
	const alerts = useAlerts();
	const [showAdvanced, setShowAdvanced] = useState(schedule !== undefined);
	const [open, setOpen] = useState(false);
	const inWizard = !!wizard;

	// the wizard asks for a rhythm, so it starts on the suggestions rather than the full form
	useEffect(() => {
		if (inWizard) setShowAdvanced(false);
	}, [inWizard]);

	const closeModal = () => (wizard ? wizard.onAbandon() : setOpen(false));
	const nextOrderDate = data && calculateNextOrderDate(data);

	const nextOrderDateString = nextOrderDate && formatCalendarDate(nextOrderDate);
	const scheduleText = data && formatScheduleText(data, locale, t);
	const disabledButton = formatScheduleText(schedule, locale, t) === '';

	const saveSchedule = async () => {
		// a subscription being put together for the first time is a draft until the customer activates it
		const status: ScheduleStatus = hasSchedule ? statusOf(schedule) : 'draft';

		const payload = {
			...schedule,
			// the carried address belongs to this walk through the wizard, not to later saves
			deliveryAddressId: schedule.deliveryAddress?.id ?? (wizard ? pendingAddress?.id : undefined),
			customerId: user?.id,
			status,
		};

		alerts.clear();
		setLoading(true);

		try {
			await axios({
				method: hasSchedule ? 'patch' : 'post',
				url: `${api}me/lists/${list.id}/schedule.json`,
				data: payload,
				withCredentials: true,
			});
		} catch (e) {
			alerts.set({ type: 'error', message: t.couldNotSave });
			return;
		} finally {
			// without this a failed request left the button reading "Sparar..." for good
			setLoading(false);
		}

		/*
		 * The address the wizard carried in is on the schedule now, so say so while revalidating -
		 * and so is the status, which the form state does not carry for a schedule being created. A
		 * cache without it reads as active, so the card left the draft steps until the revalidation
		 * landed, and stayed out of them for good if it never did.
		 */
		mutate({ ...schedule, status, deliveryAddress: schedule.deliveryAddress ?? (wizard && pendingAddress) }, true);

		if (wizard) return wizard.onDone();

		setOpen(false);
	};

	const deleteSchedule = async () => {
		if (!window.confirm(t.schedule.deleteScheduleConfirm)) return;

		const payload = {
			data: {
				...schedule,
				customerId: user?.id,
			},
			...WITH_CREDENTIALS,
		};

		alerts.clear();
		setLoading(true);

		try {
			await axios.delete(`${api}me/lists/${list.id}/schedule.json`, payload);
		} catch (e) {
			alerts.set({ type: 'error', message: t.couldNotDelete });
			return;
		} finally {
			setLoading(false);
		}

		setSchedule(DEFAULT_SCHEDULE);
		// the schedule is gone, so handing the old one back as optimistic data showed it again
		mutate(undefined, true);
		setOpen(false);
	};

	return (
		<>
			{/*
			 * What the schedule says does not depend on having a delivery address, and hiding it
			 * behind one left a customer who had just set one up with nothing to show for it - under
			 * a line telling them to choose scheduling they had already chosen.
			 *
			 * The next order is held back until there is an address, though. TCOrders'
			 * createScheduledOrders skips a schedule without one and reports it to slack, so naming
			 * a date would promise a delivery that is not going to happen.
			 */}
			{dialogOnly ? null : hasSchedule ? (
				<div className="schedule-setting">
					<strong className="schedule-setting-label">{t.stepSchedule}</strong>
					<p className="schedule-text">{scheduleText}</p>
					{/* a paused refill creates no order, so naming a date for the next one would be a promise */}
					{hasDeliveryAddress && nextOrderDate && statusOf(data) !== 'paused' && (
						<p>
							{t.schedule.nextOrder}: {nextOrderDateString}
						</p>
					)}
					<div className="schedule-setting-links">
						<button
							type="button"
							className="text-link"
							disabled={loading || deleting}
							onClick={() => {
								setShowAdvanced(true);
								setOpen(true);
							}}
						>
							{t.schedule.changeSchedule}
						</button>
						<button
							type="button"
							className="text-link"
							disabled={deleting || (loading && !open)}
							onClick={deleteSchedule}
						>
							{loading && !open ? t.schedule.deletingSchedule : t.schedule.deleteSchedule}
						</button>
					</div>
				</div>
			) : null}

			<AlertMessages alerts={alerts} />

			<Modal
				className="set-subscription-modal"
				show={open || inWizard}
				onDismiss={closeModal}
				toggle={() => (open || inWizard ? closeModal() : setOpen(true))}
			>
				<ContentCard className="set-subscription-content">
					<div className="modal-header">
						{showAdvanced && (
							<button className="close-btn" onClick={() => setShowAdvanced(false)}>
								<Icon shape={{ ...chevron_left, fill: 'black' }} size={20} />
							</button>
						)}
						<div className="modal-header-title">
							<WizardStepLabel step={wizard?.step} />
							{showAdvanced ? t.schedule.customizeScheduling : t.schedule.scheduleSubscription}
						</div>
						<button className="close-btn" onClick={closeModal}>
							<Icon shape={{ ...close, fill: 'black' }} size={20} />
						</button>
					</div>
					<div className="modal-body">
						{!showAdvanced ? (
							<SelectSuggestions
								schedule={schedule}
								setShowAdvanced={setShowAdvanced}
								setSchedule={setSchedule}
							/>
						) : (
							<EditScheduleForm schedule={schedule} setSchedule={setSchedule} />
						)}
					</div>
					<div className="modal-footer">
						<AlertMessages alerts={alerts} />
						<Button disabled={loading || disabledButton} onClick={saveSchedule}>
							{loading ? t.generic.saving : wizard ? t.wizard.next : t.generic.save}
						</Button>
					</div>
				</ContentCard>
			</Modal>
		</>
	);
};
